import React, { FunctionComponent, useMemo } from "react";
import styles from "./Spot.module.scss";
import Anchor from "components/Anchor/Anchor";
import combineClasses from "helpers/combine-classes";
import getImageCrop from "app/media/helpers/getImageCrop";
import getImageAspectRatio from "app/media/helpers/getImageAspectRatio";
import getProcessedRichtext from "blocks/helpers/getProcessedRichtext";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import isBrowserContext from "helpers/isBrowserContext";
import SpotContent from "./SpotContent.model";
import { useInView } from "react-intersection-observer";
import formatDynamicTextBreak from "blocks/helpers/formatDynamicTextBreak";

const Spots: FunctionComponent<SpotContent> = (props) => {
    const processedBodyText = getProcessedRichtext(props?.bodyText);
    const processedHeadline = getProcessedRichtext(formatDynamicTextBreak(props?.headline));

    const imageCrop = getImageCrop(props?.media?.crops, undefined, "Square (1:1)");
    const imageAspectRatio = getImageAspectRatio(imageCrop, props?.media?.width, props?.media?.height);

    const { ref, inView } = useInView({ triggerOnce: true });
    const isBrowserClass = isBrowserContext() ? styles.isBrowser : undefined;
    const inViewClass = inView ? styles.isInView : undefined;

    const link = props?.links && props?.links.length > 0 ? props?.links[0] : undefined;
    const hasLinkClass = link ? styles.has_link : undefined;

    const colorValue = props?.backgroundColor?.color;
    const bgColor = colorValue ? (colorValue.startsWith("#") ? colorValue : `#${colorValue}`) : undefined;

    const alignment = useMemo(
        () => ({
            headline: props?.textAlignHeadline ?? false,
            link: props?.textAlignLink ?? false,
            text: props?.textAlignText ?? false,
        }),
        [props?.textAlignHeadline, props?.textAlignLink, props?.textAlignText]
    );

    const enableCurves = props?.enableCurves;

    return (
        <div
            className={combineClasses([styles.wrapper, isBrowserClass, inViewClass])}
            style={{ "--background-color": bgColor } as React.CSSProperties}
            ref={ref}
        >
            <Anchor
                className={combineClasses([styles.link, hasLinkClass])}
                to={link?.link}
                target={link?.target}
            >
                <div className={styles.inner}>
                    {/* Media */}
                    {props?.media ? (
                        <div className={styles.media}>
                            <ImageViewer
                                className={styles.image}
                                src={props?.media?.url || props?.media}
                                crop={imageCrop}
                                focalPoint={props?.media?.focalPoint}
                                {...imageAspectRatio}
                                alt={props?.headline}
                            />
                        </div>
                    ) : null}

                    {/* Content */}
                    <div className={styles.content}>
                        {enableCurves && (
                            <div className={styles.shapeBox}>
                                <div className={styles.shapeBoxInner}>
                                    <svg
                                        className={styles.shapeSvg}
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 799 291"
                                        preserveAspectRatio="xMidYMid meet"
                                    >
                                        <path
                                            d="M767.023 0C775.54 0.132029 797.549 0.18817 799 0.207031V290.688H0V175.749C0.0381579 175.741 24.5757 170.769 31.166 169.449C35.9521 168.506 47.9181 165.734 57.8672 163.226C60.4676 162.584 66.1012 161.113 70.4727 159.925C79.8941 157.379 102.468 150.796 120.896 145.251C132.636 141.667 159.224 133.198 180.29 126.313C193.857 121.806 223.61 112.281 246.73 105.095C261.824 100.379 293.989 91.0622 318.598 84.291C334.821 79.7832 368.268 70.5413 393.291 63.6758C427.453 53.6605 493.837 37.4021 541.566 27.3867C557.621 23.671 588.562 17.9185 610.646 14.5234C618.013 13.2409 632.409 11.1279 642.867 9.80762C649.858 8.75138 663.387 7.09143 673.072 6.0918C679.592 5.2619 692.066 4.07451 700.903 3.39551C706.895 2.7165 718.164 1.88607 726.115 1.56543C731.504 1.13163 741.397 0.622213 748.218 0.433594C752.966 0.339287 761.389 0.15089 767.023 0Z"
                                            fill="var(--background-color)"
                                        />
                                    </svg>
                                </div>
                            </div>
                        )}
                        <div className={combineClasses([styles.text_content, !enableCurves ? styles.noCurves : ""])}>
                            {props.headline ? (
                                <h3
                                    className={combineClasses([styles.headline, alignment.headline ? styles.textAlignRight : styles.textAlignLeft])}
                                    dangerouslySetInnerHTML={processedHeadline}
                                ></h3>
                            ) : null}
                            <div
                                className={combineClasses([styles.text, alignment.text ? styles.textAlignRight : styles.textAlignLeft])}
                                dangerouslySetInnerHTML={processedBodyText}
                            ></div>
                        </div>
                    </div>

                    {link ? (
                        <div className={combineClasses([styles.arrowWrapper, alignment.link ? styles.alignRight : styles.alignLeft])}>
                            <IconViewer
                                className={styles.arrow}
                                name="arrowRight"
                                heightRatio={10.848 / 31.519}
                            />
                        </div>
                    ) : null}
                </div>
            </Anchor>
        </div>
    );
};

export default Spots;
